import { Box, Button, HStack, Stack, Text, Flex } from "@chakra-ui/react";
import { FiFilter } from "react-icons/fi";
import React, {  useRef, useState, useEffect } from "react";
import AuditTable from "./AuditLog/AuditTable";
import { getAuditLogData } from "../services/AuditLog";
import SearchBox from "../CommonComponents/SearchBox";
import Pagination from "./DeriveFile/Pagination";
import { FilterBox } from "../CommonComponents/Filter/FilterBox";
import { totalFilterCount, updateFacetsData } from "../util/helper";
import { common, explore } from "../constants/constants";
import FilterPills from "../CommonComponents/Filter/FilterPills";
import SortBy from "../CommonComponents/Filter/SortBy";

export default function AuditLog() {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [facets, setFacets] = useState([]);
  const [apply, setApply] = useState(false);
  const [filterCount, setFilterCount] = useState(0);
  const [reset, setReset] = useState(false);
  const [filteredResultCount, setFilteredResultCount] = useState(0);
  const [sort, setSort] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    search: "",
    filters: {},
    gate:"AND",
    sortBy_v:""
  });
  const [filterPills, setFilterPills] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 10;
  
  const fetchFiles = async (page, searchText, filterOptions) => {
    setLoading(true);
    try {
      const result = await getAuditLogData(page, limit, searchText, filterOptions);
      setData(result?.data || []);
      setTotalPages(Math.ceil(result?.total / limit));
      setTotalRecords(result?.total || 0);
      
      // Handle facets
      if (result?.facetsHeaders && result?.facets) {
        const formattedFacets = updateFacetsData({ data: result });
        setFacets(formattedFacets);
        
        if (formattedFacets && formattedFacets.length > 0 && currentPage === 1) {
          setFilterOptions((prevFilterOptions) => ({
            ...prevFilterOptions,
            filters: {
              ...prevFilterOptions.filters,
              ...formattedFacets.reduce((acc, facet) => {
                acc[facet.key] = [];
                return acc;
              }, {}),
            },
          }));
        }
      }
      
      // Handle sort options
      const sortedFacets = result?.sortByHeader?.map((obj) => {
        const key = Object.keys(obj)[0];
        return { key: key, display: obj[key] };
      });
      setSort(sortedFacets);
      
      if (
        result?.page_number &&
        parseInt(result?.page_number) !== currentPage
      ) {
        setCurrentPage(parseInt(result?.page_number));
      }
    } catch (err) { 
      console.error("Fetch error:", err);
      setData([]);
    }
    setLoading(false);
  };
  // Handle page changes
  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(parseInt(page));
    }
  };

  const columns = [
    { key: "createdAt", label: "Date " },
    { key: "admin_user_id", label: "User name" },
    { key: "action", label: "Action" },
    { key: "entity", label: "Entity" },
    { key: "record_alias", label: "Record Alias" },
    { key: "changes", label: "Changes " },
  ];

  const isInitialLoad = useRef(true);
  
  const handleSearch = (searchTerm) => {
    if (searchTerm.trim()) {
      isInitialLoad.current = false;
      setCurrentPage(1);
      handleCheckboxChange("searches", searchTerm);
      setSearchText(searchTerm);
      setApply(true);
    }
  };
  
  const handleClear = () => {
    handleReset();
    setApply(true);
  };

  const handleCheckboxChange = (value, type) => {
    let selectedArray = [];
    let selectedString = "";  
    switch (value) {
      case "sort":
        selectedString = filterOptions.sortBy_v;
        break;
      case "gate":
        selectedString = filterOptions.gate;
        break;
      case "searches":
        selectedString = filterOptions.search;
        break;
      default:
        if (value in filterOptions.filters) {
          selectedArray = filterOptions.filters[value];
        }
        break;
    }
  
    if (Array.isArray(type)) {
      // Bulk replace (used by FilterBody's "Select All" / clear-all toggle)
      // rather than the usual single-value toggle below.
      selectedArray = type;
    } else if (selectedArray.includes(type)) {
      selectedArray = selectedArray.filter((checkbox) => checkbox !== type);
    } else {
      selectedArray = [...selectedArray, type];
    }
  
    if (selectedString !== type) {
      selectedString = type;
    }
  
    switch (value) {
      case "sort":
        setFilterOptions(prevFilterOptions => ({
          ...prevFilterOptions,
          sortBy_v: selectedString,
        }));
        break;
      case "gate":
        setFilterOptions(prevFilterOptions => ({
          ...prevFilterOptions,
          gate: selectedString,
        }));
        break;
      case "searches":
        setFilterOptions(prevFilterOptions => ({
          ...prevFilterOptions,
          search: selectedString,
        }));
        break;
      default:
        if (value in filterOptions.filters) {
          setFilterOptions(prevFilterOptions => ({
            ...prevFilterOptions,
            filters: {
              ...prevFilterOptions.filters,
              [value]: selectedArray,
            },
          }));
        }
        break;
    }
  };

  const handleReset = () => {
    setFilteredResultCount(0);
    const initializedFilters = facets.reduce((acc, facet) => {
      acc[facet.key] = [];
      return acc;
    }, {});
    setFilterOptions({
      search: "",
      filters: initializedFilters,
      sortBy_v: "",
      gate:"AND"
    });
    setFilterPills({
      search: "",
      filters: initializedFilters,
      sortBy_v: "",
      gate:"AND"
    });
    setFilterCount(0);
    setSearchText("");
    setCurrentPage(1);
    setReset(true);
  };

  async function fetchFilterData(page = 1) {
    try {
      setLoading(true);
      const result = await getAuditLogData(page, limit, searchText, filterOptions);
      setLoading(false);
      if (result) {
        setData(result?.data || []);
        setTotalPages(Math.ceil(result?.total / limit));
        setTotalRecords(result?.total || 0);
       
        const filtercnt = totalFilterCount(filterOptions?.filters);
        if(filtercnt > 0 || filterOptions?.search){
          setFilteredResultCount(result?.total);
        } else {
          setFilteredResultCount(0);
        }

        if (result?.facetsHeaders && result?.facets) {
          const formattedFacets = updateFacetsData({ data: result });
          setFacets(formattedFacets);
          if (formattedFacets && formattedFacets.length > 0 && reset) {
            setFilterOptions((prevFilterOptions) => ({
              ...prevFilterOptions,
              filters: {
                ...prevFilterOptions.filters,
                ...formattedFacets.reduce((acc, facet) => {
                  acc[facet.key] = [];
                  return acc;
                }, {}),
              },
            }));
            setReset(false);
          }
        }

        const sortedFacets = result?.sortByHeader?.map((obj) => {
          const key = Object.keys(obj)[0];
          return { key: key, display: obj[key] };
        });
        setSort(sortedFacets);
      }
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  }

  useEffect(() => {
    fetchFiles(currentPage, searchText, filterOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (apply) {
      window.scrollTo(0, 0);
      setApply(false);
      setCurrentPage(1);
      fetchFilterData(1);
      setFilterPills(filterOptions);
    }
    const filtercnt = totalFilterCount(filterOptions?.filters);
    setFilterCount(filtercnt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apply]);
  return (
    <>
      <HStack width={"100%"} alignItems={'self-start'} justifyContent={"space-between"}>
        <Box 
          px={"20px"}
          pt={"5px"} 
          pb={"50px"} 
          width="100%" 
          mx="auto" 
          padding={"20px"} 
          bg={"white"}
          height="calc(100vh - 120px)"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <Box 
            position={"sticky"} 
            zIndex={99} 
            top={"0px"} 
            bg="white" 
            display="flex" 
            justifyContent="space-between"
            alignItems="center"
            py={1}
            px={2}
            boxShadow="sm"
            border="1px solid"
            borderColor="gray.100"
            flexShrink={0}
          >
            <Flex alignItems="center" gap={4}>
              <Text
                fontSize="24px" 
                color="gray.800" 
                fontWeight="700"
                width={"max-content"}
                letterSpacing="-0.5px"
              >
                Audit Log
              </Text>
              {totalRecords > 0 && (
                <Box
                  display="inline-flex"
                  alignItems="center"
                  bg="blue.50"
                  color="blue.700"
                  fontSize="md"
                  width={"max-content"}
                  fontWeight="600"
                  px={3}
                  py={1.5}
                  borderRadius="full"
                  border="1px solid"
                  borderColor="blue.200"
                  boxShadow="sm"
                >
                  <Text width={"max-content"}>{totalRecords.toLocaleString()} {totalRecords === 1 ? 'Record' : 'Records'}</Text>
                </Box>
              )}
            </Flex>
            <Flex flexWrap={"wrap"} justifyContent={"flex-end"} alignItems={"baseline"} gap={"10px"}>
              <Box display="flex" gap={2} flexShrink={0}>
                <SearchBox onSearch={handleSearch} onClear={handleClear} type="audit-log" />
              </Box>
              {facets && facets.length > 0 && (
                <Button
                  size="sm"
                  fontSize="14px"
                  fontWeight="600"
                  onClick={() => setShowFilter(!showFilter)}
                  leftIcon={<FiFilter size={16} />}
                  variant={showFilter ? "solid" : "outline"}
                  borderColor="blue.300"
                  color={showFilter ? "white" : "blue.600"}
                  bg={showFilter ? "blue.600" : "white"}
                  _hover={{
                    bg: showFilter ? "blue.700" : "blue.50",
                    borderColor: "blue.400",
                    color: showFilter ? "white" : "blue.700",
                    transform: "translateY(-1px)",
                    boxShadow: "sm"
                  }}
                  _active={{
                    transform: "translateY(0)",
                    bg: showFilter ? "blue.800" : "blue.100"
                  }}
                  transition="all 0.2s ease"
                  borderRadius="lg"
                  px={4}
                  py={2}
                  height="36px"
                  flexShrink={0}
                >
                  {common.FILTER}
                </Button>
              )}
              <Pagination handlePageChange={handlePageChange} currentPage={currentPage} totalPages={totalPages} />
            </Flex>
          </Box>
          
          {sort && sort?.length > 0 && data && data?.length > 0 && filterOptions && (
            <HStack mb="10px" justifyContent={"flex-end"}>
              <SortBy
                sort={sort}
                filterOptions={filterOptions}
                handleCheckboxChange={handleCheckboxChange}
                setApply={setApply}
              />
            </HStack>
          )}
          
          {((filterPills && filterPills?.search) || filterCount > 0) && (
            <Box position={"sticky"} top={"53px"} bg="white" zIndex={9} width={"100%"}>
              {((filterPills && filterPills?.search !== '') || filterCount > 0) && 
                <FilterPills
                  filters={filterPills}
                  setFilterOptions={setFilterOptions}
                  setApply={setApply}
                  setSearchTerm={setSearchText}
                  setFilterPills={setFilterPills}
                  filteredResultCount={filteredResultCount}
                />
              }
            </Box>
          )}
          
          <Box mt={4} flex="1" overflow="auto">
            <AuditTable
              fetchFiles={fetchFiles}
              data={data}
              currentPage={currentPage}
              searchText={searchText}
              loading={loading}
              columns={columns}
            />
          </Box>
        </Box>
        
        {showFilter && (
          <Stack
            marginRight={"0px!important"}
            right={"0px"}
            top={"20px"}
            pos={'sticky'}
            pt="4"
          >
            {facets && facets?.length > 0 && (
              <FilterBox
                setShowFilterBox={setShowFilter}
                setApply={setApply}
                explore={explore}
                handleCheckboxChange={handleCheckboxChange}
                handleSearch={handleSearch}
                filterOptions={filterOptions}
                filteroptionData={facets}
                setFilterOptions={setFilterOptions}
                setSearchTerm={setSearchText}
                filterCount={filterCount}
                searchTerm={searchText}
                handleReset={handleReset}
                className={"box-filter-film"}
                sort={sort}
              />
            )}
          </Stack>
        )}
      </HStack>
    </>
  );
}
